import { createModel } from 'ice';
import monsole from '@/utils/monsole';
import { queryLabelTree } from './service';
import { flattenTree } from './tagTree';
import { MAX_SEARCH_HISTORY, loadSearchHistory, saveSearchHistory } from './searchHistoryStorage';
import type { TagNode, TagKind } from './types';

/**
 * 录音上传成功后触发待补录列表刷新的延迟。
 * 后端写 voice_url 有延迟，立即查会拿不到，故等一拍再拉。
 */
const HISTORY_RELOAD_DELAY_MS = 1000;

export interface RecordState {
  /** FST 标签（扁平化，null 表示尚未加载） */
  fst: TagNode[] | null;
  /** FIT 标签（扁平化，null 表示尚未加载） */
  fit: TagNode[] | null;
  loadingFst: boolean;
  loadingFit: boolean;
  /** 最近一次加载失败信息 */
  error: string | null;
  /** 搜索历史（持久化到 localStorage，刷新不丢），fst / fit 各自独立 */
  searchHistory: Record<TagKind, string[]>;
  /** 待补录列表的刷新信号：自增即触发 `useRecordHistory` 重拉 */
  historyNonce: number;
}

const initialState: RecordState = {
  fst: null,
  fit: null,
  loadingFst: false,
  loadingFit: false,
  error: null,
  searchHistory: loadSearchHistory(),
  historyNonce: 0,
};

export default createModel({
  state: initialState,

  reducers: {
    setTags(prevState: RecordState, payload: { kind: TagKind; list: TagNode[] }) {
      if (payload.kind === 'fst') prevState.fst = payload.list;
      else prevState.fit = payload.list;
    },

    setLoading(prevState: RecordState, payload: { kind: TagKind; loading: boolean }) {
      if (payload.kind === 'fst') prevState.loadingFst = payload.loading;
      else prevState.loadingFit = payload.loading;
    },

    setError(prevState: RecordState, payload: string | null) {
      prevState.error = payload;
    },

    // 去重/截断只在这一处算，故写回也放在这里，避免 effect 里重复实现一遍
    addHistory(prevState: RecordState, payload: { kind: TagKind; term: string }) {
      const t = payload.term.trim();
      if (!t) return;
      const prev = prevState.searchHistory[payload.kind];
      const next = [t, ...prev.filter(x => x !== t)].slice(0, MAX_SEARCH_HISTORY);
      prevState.searchHistory[payload.kind] = next;
      saveSearchHistory(payload.kind, next);
    },

    clearHistory(prevState: RecordState, payload: { kind: TagKind }) {
      prevState.searchHistory[payload.kind] = [];
      saveSearchHistory(payload.kind, []);
    },

    // 返回 state：ice 只有在 reducer 返回类型等于入参类型时才推导出零参 dispatcher
    bumpHistoryNonce(prevState: RecordState): RecordState {
      prevState.historyNonce += 1;
      return prevState;
    },
  },

  effects: () => ({
    /** 拉取单个 kind 的标签树并扁平化 */
    async loadTag(kind: TagKind) {
      this.setLoading({ kind, loading: true });
      this.setError(null); // 新一次尝试开始即清掉上一次的失败信息
      try {
        const raw = await queryLabelTree(kind);
        // 接口 1 返回单个根节点（树容器），其 children 才是可选标签；根节点自身不作为标签展示
        const roots = raw?.children ?? [];
        if (!roots.length) monsole.warn('[record] label tree root empty', { kind, rootId: raw?.id });
        const list = flattenTree(roots);
        this.setTags({ kind, list });
      } catch (e: any) {
        const msg = e?.message ?? '标签加载失败';
        this.setError(msg);
        monsole.error('[record] loadTag fail', { kind }, msg);
        throw e;
      } finally {
        this.setLoading({ kind, loading: false });
      }
    },

    /** 并发补齐缺失的 kind（由组件侧判断哪些缺失后传入，避免在 effect 内读 state） */
    async ensureTags(missing: TagKind[]) {
      if (!missing.length) return;
      await Promise.allSettled(missing.map(kind => this.loadTag(kind)));
    },

    addSearchHistory(payload: { kind: TagKind; term: string }) {
      this.addHistory(payload);
    },

    clearSearchHistory(payload: { kind: TagKind }) {
      this.clearHistory(payload);
    },

    /**
     * 录音上传成功后刷新待补录列表（顶栏角标与抽屉共用一份数据源）。
     * 定时器挂在 store 而非调用方组件上：面板上传成功即卸载，挂在组件里会被一起清掉。
     */
    requestHistoryReload() {
      setTimeout(() => this.bumpHistoryNonce(), HISTORY_RELOAD_DELAY_MS);
    },
  }),
});
